import { useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { useTranslation, useLocale } from "@/lib/i18n";
import {
  MaritimeArea,
  useMaritimeAreasQuery,
} from "@/features/dashboard/components/warnings/maritime-areas.queries";
import { MARITIME_FORECAST_DOMAINS } from "@/features/dashboard/components/warnings/maritime-forecast.domains";
import { useMaritimeForecastQuery } from "@/features/dashboard/components/warnings/maritime-forecast.queries";
import { getAreaName } from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import {
  createDateTimeKeys,
  isEmpty,
  plotSeriePoints,
} from "@/features/shared/widgets/components/time-series-table/components/table.utils";
import type { WarningColor } from "@/features/shared/widgets/components/time-series-table/components/types";
import useRefetchMaritimeForecast from "@/features/shared/widgets/hooks/useRefetchMaritimeForecast";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import { localizeTime } from "@/features/shared/widgets/helpers";
import {
  formatSeichesCellValue,
  formatSeichesTooltip,
  getSeichesColorCodeForValue,
} from "@/features/shared/widgets/seiches/seiches-display.utils";
import type { MaritimeSeichesWidgetConfiguration } from "@/lib/api";

import {
  buildMaritimeSeichesViewModel,
  type MaritimeSeichesViewModel,
} from "./maritime-seiches-forecast.utils";
import styles from "./MaritimeSeiches.module.css";

type Props = {
  id: string;
  data: unknown;
  configuration: MaritimeSeichesWidgetConfiguration;
};

const MaritimeSeiches = ({ configuration }: Props) => {
  const { t } = useTranslation();
  const locale = useLocale();

  useRefetchMaritimeForecast(MARITIME_FORECAST_DOMAINS.coastal);

  const forecastQuery = useMaritimeForecastQuery(MARITIME_FORECAST_DOMAINS.coastal);
  const areasQuery = useMaritimeAreasQuery();

  const areaId = configuration.area_id;

  const viewModel = useMemo(() => {
    const forecast = forecastQuery.data;
    const areas = areasQuery.data;
    if (!forecast || !areas || !areaId) return null;

    const area = areas.find((a: MaritimeArea) => a.id === areaId);
    const areaName = area ? getAreaName(area, locale) : areaId;

    return buildMaritimeSeichesViewModel(forecast, areaId, areaName, locale);
  }, [forecastQuery.data, areasQuery.data, areaId, locale]);

  const resolveCell = useCallback(
    (date: Date) => viewModel?.cellsByPeriodStart[date.toISOString()],
    [viewModel?.cellsByPeriodStart],
  );

  const formatValue = useCallback(
    (value: number, date: Date) => {
      const cell = resolveCell(date);
      const subtype = value > 0.99 ? (cell?.applicableSubtype ?? null) : undefined;
      return formatSeichesCellValue(value, t, subtype);
    },
    [resolveCell, t],
  );

  const getTooltip = useCallback(
    (value: number, date: Date) => {
      const cell = resolveCell(date);
      const subtype = value > 0.99 ? (cell?.applicableSubtype ?? null) : undefined;
      return formatSeichesTooltip(value, t, cell?.message, subtype);
    },
    [resolveCell, t],
  );

  if (forecastQuery.isLoading || areasQuery.isLoading) {
    return null;
  }

  if (forecastQuery.isError || !viewModel) {
    return <span>{t("widgets.no_data")}</span>;
  }

  return (
    <MaritimeSeichesGrid
      viewModel={viewModel}
      formatValue={formatValue}
      getTooltip={getTooltip}
      getColorCodeForValue={getSeichesColorCodeForValue}
    />
  );
};

export default MaritimeSeiches;

type CellStyle = {
  backgroundColor: string;
  color: string;
  hoverColor: string;
};

const getFooterDates = (dates: Date[], timezone: string) => {
  const shownDates = new Set<string>();

  return dates.reduce<string[]>((acc, date) => {
    const localizedDate = localizeTime(date, "d MMM", timezone) ?? "";
    const day = localizeTime(date, "d", timezone) ?? "";

    if (day && !shownDates.has(day)) {
      shownDates.add(day);
      acc.push(String(localizedDate));
    } else {
      acc.push("");
    }

    return acc;
  }, []);
};

const getCellStyle = (colorCode: WarningColor, theme: Theme): CellStyle => {
  const backgroundColor =
    colorCode !== "none" ? (theme.palette[colorCode]?.main ?? "white") : "white";
  const hasBackground = backgroundColor !== "white";
  const hoverColor = hasBackground ? "white" : "#3a3a3a";
  const color = hasBackground ? "white" : `${hoverColor}66`;

  return { backgroundColor, color, hoverColor };
};

type GridProps = {
  viewModel: MaritimeSeichesViewModel;
  formatValue: (value: number, date: Date) => string;
  getTooltip: (value: number, date: Date) => string;
  getColorCodeForValue: (value: number) => WarningColor;
};

const MaritimeSeichesGrid = ({
  viewModel,
  formatValue,
  getTooltip,
  getColorCodeForValue,
}: GridProps) => {
  const theme = useTheme();
  const timezone = useWidgetTimezone();

  const serie = viewModel.series[0];
  const dateTimeKeys = useMemo(
    () => createDateTimeKeys(viewModel.timeRange),
    [viewModel.timeRange],
  );
  const plottedSerie = useMemo(
    () => plotSeriePoints(serie, viewModel.timeRange),
    [serie, viewModel.timeRange],
  );

  const tzOrOffset = useMemo(() => {
    if (!timezone) return undefined;
    return timezone.name === "UTC" ? timezone.offset : timezone.name;
  }, [timezone]);
  const footerDates = useMemo(
    () => (tzOrOffset ? getFooterDates(dateTimeKeys, tzOrOffset) : []),
    [dateTimeKeys, tzOrOffset],
  );

  const gridTemplateColumns = `max-content repeat(${dateTimeKeys.length}, minmax(0, 1fr))`;

  let dataColSpan = 0;

  return (
    <div className={styles.grid} style={{ gridTemplateColumns }}>
      <div className={styles.label}>{serie.name}</div>
      {dateTimeKeys.map((date) => {
        dataColSpan--;

        if (dataColSpan > 0) return null;

        const point = plottedSerie[date.toISOString()];
        dataColSpan = point?.span ?? 1;

        const value = point?.value;
        const hasValue = !isEmpty(value);
        const colorCode = hasValue ? getColorCodeForValue(value) : "none";
        const cellStyle = getCellStyle(colorCode, theme);

        return (
          <div
            key={`data-${date.toISOString()}`}
            className={styles.dataCell}
            style={{
              gridColumn: `span ${dataColSpan}`,
              backgroundColor: cellStyle.backgroundColor,
              color: cellStyle.color,
              borderColor: cellStyle.backgroundColor !== "white" ? "white" : "transparent",
              ["--seiches-cell-hover-color" as string]: cellStyle.hoverColor,
            }}
            title={hasValue ? getTooltip(value, date) : "-"}
          >
            {hasValue ? formatValue(value, date) : "-"}
          </div>
        );
      })}

      {timezone && (
        <>
          <div className={styles.footerLabel}>uur</div>
          {dateTimeKeys.map((date) => {
            const hour = `${localizeTime(date, "H", tzOrOffset)}`;
            const showHour =
              dateTimeKeys.length < 18 ||
              (dateTimeKeys.length < 36 && parseInt(hour, 10) % 2 === 0) ||
              parseInt(hour, 10) % 4 === 0;

            return (
              <div key={`hour-${date.toISOString()}`} className={styles.footerCell}>
                {showHour ? hour : null}
              </div>
            );
          })}

          <div className={styles.footerLabel} />
          {footerDates.map((dateLabel, index) => (
            <div
              key={`date-${dateTimeKeys[index].toISOString()}`}
              className={styles.footerCell}
            >
              {dateLabel}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
